import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, IconButton, useTheme, useMediaQuery,
  BottomNavigation, BottomNavigationAction, SwipeableDrawer, Fab,
  AppBar, Toolbar, Divider, Slide, Zoom, Tab, Tabs
} from '@mui/material';
import {
  TouchApp as ManualIcon,
  PhotoCameraFront as FinishLynxIcon,
  Description as HyTekIcon,
  KeyboardArrowUp as ExpandIcon,
  KeyboardArrowDown as CollapseIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Close as CloseIcon
} from '@mui/icons-material';

import FinishLynxIntegration from './integraciones/FinishLynxIntegration';
import { EventProvider } from '../../context/EventContext';

/* ---------- util ---------- */
function TabPanel({ children, value, index, fullScreen = false }) {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));

  if (value !== index) return null;
  
  return (
    <Box 
      sx={{ 
        p: isSmall ? 1 : (fullScreen ? 0 : 2),
        height: fullScreen ? '100%' : 'auto',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {children}
    </Box>
  );
}

/* ----------------------------------------- */
const IntegracionesManager = ({ competitionId: propId }) => {
  /* ----- IDs y navegación ----- */
  const { competitionId: urlId } = useParams();
  const competitionId = propId || urlId;
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isXSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const drawerRef = useRef(null);

  /* ----- Estados ----- */
  const [selectedTab, setSelectedTab] = useState(0);
  const [fullScreen, setFullScreen] = useState(false);
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false);
  const [bottomSheetHeight, setBottomSheetHeight] = useState(300); // Altura inicial

  // Define los elementos de navegación
  const navigationItems = [
    { icon: <ManualIcon />, label: "Manual" },
    { icon: <FinishLynxIcon />, label: "FinishLynx" },
    { icon: <HyTekIcon />, label: "Hy-Tek" }
  ];

  /* ► Sincroniza el tab cada vez que cambie la URL ◄ */
  useEffect(() => {
    if (pathname.includes('/integraciones/finishlynx')) setSelectedTab(1);
    else if (pathname.includes('/integraciones/hytek')) setSelectedTab(2);
    else setSelectedTab(0);
  }, [pathname]);

  /* Al cambiar de tab actualizamos la URL para que quede reflejada */
  const handleTabChange = (_, newValue) => {
    setSelectedTab(newValue);
    const base = `/${competitionId}/integraciones`;
    if (newValue === 1) navigate(`${base}/finishlynx`, { replace: false });
    else if (newValue === 2) navigate(`${base}/hytek`, { replace: false });
    else navigate(`${base}/manual`, { replace: false });
  };

  // Funciones para controlar el Bottom Sheet
  const toggleBottomSheet = () => {
    setBottomSheetOpen(!bottomSheetOpen);
  };

  const expandBottomSheet = () => {
    setBottomSheetHeight(window.innerHeight * 0.8); // 80% de la altura de la ventana
  };
  
  const collapseBottomSheet = () => {
    setBottomSheetHeight(300); // Tamaño predeterminado más pequeño
  };

  const toggleFullScreen = () => {
    setFullScreen(!fullScreen);
  };

  /* ----- verificación de ID ----- */
  if (!competitionId) {
    return (
      <Paper sx={{ p: 3, mt: 2 }}>
        <Typography color="error">
          Error: No se ha especificado una competencia para gestionar las
          integraciones.
        </Typography>
      </Paper>
    );
  }

  /* ----- render ----- */
  return (
    <EventProvider competitionId={competitionId}>
      <Box sx={{ 
        width: '100%', 
        mt: fullScreen ? 0 : 2,
        height: fullScreen ? '100vh' : 'auto',
        position: fullScreen ? 'fixed' : 'relative',
        top: fullScreen ? 0 : 'auto',
        left: fullScreen ? 0 : 'auto',
        right: fullScreen ? 0 : 'auto',
        bottom: fullScreen ? 0 : 'auto',
        zIndex: fullScreen ? 1300 : 1,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper'
      }}>
        {/* AppBar superior */}
        <AppBar 
          position="static" 
          color={fullScreen ? "primary" : "default"} 
          elevation={fullScreen ? 2 : 0}
          sx={{ 
            borderTopLeftRadius: fullScreen ? 0 : theme.shape.borderRadius,
            borderTopRightRadius: fullScreen ? 0 : theme.shape.borderRadius,
          }}
        >
          <Toolbar variant={isXSmall ? "dense" : "regular"}>
            <Typography 
              variant={isXSmall ? "h6" : "h5"} 
              component="div" 
              sx={{ 
                flexGrow: 1,
                color: fullScreen ? '#fff' : theme.palette.text.primary 
              }}
            >
              Integraciones
            </Typography>
            
            <IconButton 
              onClick={toggleFullScreen}
              color={fullScreen ? "inherit" : "primary"}
              sx={{ ml: 1 }}
              edge="end"
            >
              {fullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Toolbar>
          
          {/* Tabs para pantallas grandes */}
          {!isMobile && (
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant="fullWidth"
              aria-label="tabs integraciones"
              sx={{ 
                bgcolor: theme.palette.background.paper,
                borderRadius: fullScreen ? 0 : theme.shape.borderRadius,
              }}
            >
              <Tab 
                icon={<ManualIcon />} 
                iconPosition="start" 
                label="Manual" 
              />
              <Tab 
                icon={<FinishLynxIcon />} 
                iconPosition="start" 
                label="FinishLynx" 
              />
              <Tab 
                icon={<HyTekIcon />} 
                iconPosition="start" 
                label="Hy-Tek" 
              />
            </Tabs>
          )}
        </AppBar>

        {/* Contenido principal */}
        <Box 
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            height: isMobile ? `calc(100vh - ${fullScreen ? 56 : 112}px - ${bottomSheetOpen ? 0 : 56}px)` : 'auto',
            transition: 'height 0.3s ease',
            pb: isMobile && !bottomSheetOpen ? 7 : 0 // Espacio para la barra de navegación inferior
          }}
        >
          {/* Contenido de cada tab */}
          <TabPanel value={selectedTab} index={0} fullScreen={fullScreen}>
            <Typography variant="h6" gutterBottom>Modo Manual</Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography paragraph>
              Contenido del modo manual
            </Typography>
          </TabPanel>

          <TabPanel value={selectedTab} index={1} fullScreen={fullScreen}>
            <FinishLynxIntegration 
              fullScreen={fullScreen || bottomSheetOpen} 
              isBottomSheet={bottomSheetOpen}
              isMobileView={isMobile}
            />
          </TabPanel>

          <TabPanel value={selectedTab} index={2} fullScreen={fullScreen}>
            <Typography variant="h6" gutterBottom>Integración Hy-Tek</Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography paragraph>
              Contenido de la integración Hy-Tek
            </Typography>
          </TabPanel>
        </Box>

        {/* BottomNavigation para móvil */}
        {isMobile && !bottomSheetOpen && (
          <Paper 
            sx={{ 
              position: 'fixed', 
              bottom: 0, 
              left: 0, 
              right: 0,
              zIndex: 100,
              borderRadius: 0
            }} 
            elevation={3}
          >
            <BottomNavigation
              value={selectedTab}
              onChange={handleTabChange}
              showLabels={!isXSmall}
            >
              {navigationItems.map((item, index) => (
                <BottomNavigationAction 
                  key={index}
                  label={isXSmall ? null : item.label} 
                  icon={item.icon} 
                />
              ))}
            </BottomNavigation>
          </Paper>
        )}

        {/* Botón para abrir el Bottom Sheet en móvil */}
        {isMobile && !bottomSheetOpen && (
          <Zoom in={!bottomSheetOpen}>
            <Fab
              color="primary"
              aria-label="expand"
              onClick={toggleBottomSheet}
              sx={{
                position: 'fixed',
                bottom: 70,
                right: 16,
                zIndex: 101
              }}
            >
              <ExpandIcon />
            </Fab>
          </Zoom>
        )}
        
        {/* Bottom Sheet para vista detallada en móvil */}
        {isMobile && (
          <SwipeableDrawer
            ref={drawerRef}
            anchor="bottom"
            open={bottomSheetOpen}
            onClose={toggleBottomSheet}
            onOpen={toggleBottomSheet}
            disableSwipeToOpen
            PaperProps={{
              sx: {
                height: bottomSheetHeight,
                overflow: 'visible',
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                transition: 'height 0.3s ease'
              }
            }}
            sx={{
              '& .MuiDrawer-paper': {
                height: bottomSheetHeight
              }
            }}
          >
            {/* Barra de control del Bottom Sheet */}
            <Box
              sx={{
                position: 'absolute',
                top: -20,
                left: 0,
                right: 0,
                display: 'flex',
                justifyContent: 'center',
                zIndex: 1
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  width: 120,
                  height: 40,
                  borderRadius: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2
                }}
              >
                <IconButton 
                  size="small" 
                  onClick={bottomSheetHeight > 300 ? collapseBottomSheet : expandBottomSheet}
                >
                  {bottomSheetHeight > 300 ? <CollapseIcon /> : <ExpandIcon />}
                </IconButton>
                <IconButton 
                  size="small" 
                  onClick={toggleBottomSheet}
                >
                  <CloseIcon />
                </IconButton>
              </Paper>
            </Box>
            
            {/* Cabecera del Bottom Sheet */}
            <Box sx={{ 
              p: 2, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              borderBottom: `1px solid ${theme.palette.divider}`
            }}>
              <Typography variant="h6">
                {navigationItems[selectedTab]?.label || "Detalle"}
              </Typography>
              <IconButton onClick={toggleFullScreen}>
                {fullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Box>
            
            {/* Contenido del Bottom Sheet */}
            <Box
              sx={{
                height: 'calc(100% - 60px)',
                overflow: 'auto',
                p: 2
              }}
            >
              {/* Renderiza el mismo contenido, pero en el contexto del bottom sheet */}
              {selectedTab === 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>Modo Manual</Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography paragraph>
                    Contenido del modo manual
                  </Typography>
                </Box>
              )}
              
              {selectedTab === 1 && (
                <FinishLynxIntegration 
                  fullScreen={true} 
                  isBottomSheet={true}
                  isMobileView={true}
                />
              )}
              
              {selectedTab === 2 && (
                <Box>
                  <Typography variant="h6" gutterBottom>Integración Hy-Tek</Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography paragraph>
                    Contenido de la integración Hy-Tek
                  </Typography>
                </Box>
              )}
            </Box>
          </SwipeableDrawer>
        )}
      </Box>
    </EventProvider>
  );
};

export default IntegracionesManager;